import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { 
  FileText, 
  ClipboardCheck, 
  UserCheck, 
  Shuffle, 
  Activity, 
  CheckCircle2,
  Receipt,
  ArrowRight,
  Users
} from "lucide-react";

interface TrialFlowPipelineProps {
  trialSiteId: string;
  trialId?: string;
}

interface StageData {
  id: string;
  label: string;
  icon: React.ElementType;
  count: number;
  color: string;
}

export function TrialFlowPipeline({ trialSiteId, trialId }: TrialFlowPipelineProps) {
  const { t } = useTranslation();
  // Fetch patient counts by stage
  const { data: stageCounts } = useQuery({
    queryKey: ['trial-flow-pipeline', trialSiteId, trialId],
    queryFn: async () => {
      const statuses = ['screening', 'consent', 'enrolled', 'randomized', 'active', 'completed', 'withdrawn'];
      const counts: Record<string, number> = {};

      for (const status of statuses) {
        let query = supabase
          .from('patient_trial_status')
          .select('*', { count: 'exact', head: true })
          .eq('status', status);

        if (trialId) {
          query = query.eq('trial_id', trialId);
        }

        const { count } = await query;
        counts[status] = count || 0;
      }

      // Get matched count from patient_matches
      let matchQuery = supabase
        .from('patient_matches')
        .select('*', { count: 'exact', head: true })
        .in('status', ['new', 'pre_screened', 'interested', 'notified']);

      if (trialId) {
        matchQuery = matchQuery.eq('trial_id', trialId);
      }

      const { count: matchCount } = await matchQuery;
      counts['matched'] = matchCount || 0;

      // Get billed count
      let billQuery = supabase
        .from('billable_events')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'approved');

      if (trialId) {
        billQuery = billQuery.eq('trial_site_id', trialSiteId);
      }

      const { count: billCount } = await billQuery;
      counts['billed'] = billCount || 0;

      return counts;
    },
  });

  const stages: StageData[] = [
    { id: 'matched', label: t('trialSite.components.flowPipeline.stages.matched'), icon: Users, count: stageCounts?.matched || 0, color: 'bg-blue-500' },
    { id: 'screening', label: t('trialSite.components.flowPipeline.stages.screening'), icon: ClipboardCheck, count: stageCounts?.screening || 0, color: 'bg-yellow-500' },
    { id: 'consent', label: t('trialSite.components.flowPipeline.stages.consent'), icon: FileText, count: stageCounts?.consent || 0, color: 'bg-orange-500' },
    { id: 'enrolled', label: t('trialSite.components.flowPipeline.stages.enrolled'), icon: UserCheck, count: stageCounts?.enrolled || 0, color: 'bg-indigo-500' },
    { id: 'randomized', label: t('trialSite.components.flowPipeline.stages.randomized'), icon: Shuffle, count: stageCounts?.randomized || 0, color: 'bg-purple-500' },
    { id: 'active', label: t('trialSite.components.flowPipeline.stages.active'), icon: Activity, count: stageCounts?.active || 0, color: 'bg-green-500' },
    { id: 'completed', label: t('trialSite.components.flowPipeline.stages.completed'), icon: CheckCircle2, count: stageCounts?.completed || 0, color: 'bg-emerald-600' },
    { id: 'billed', label: t('trialSite.components.flowPipeline.stages.billed'), icon: Receipt, count: stageCounts?.billed || 0, color: 'bg-teal-600' },
  ];

  const totalPatients = stages.reduce((sum, s) => sum + s.count, 0);
  const maxCount = Math.max(...stages.map(s => s.count), 1);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Activity className="h-5 w-5" />
          {t('trialSite.components.flowPipeline.title')}
          <Badge variant="secondary" className="ml-2">{t('trialSite.components.flowPipeline.total', { n: totalPatients })}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Funnel visualization */}
        <div className="space-y-2">
          {stages.map((stage, index) => {
            const Icon = stage.icon;
            const barWidth = maxCount > 0 ? Math.max((stage.count / maxCount) * 100, 8) : 8;
            
            return (
              <div key={stage.id} className="flex items-center gap-3">
                <div className="flex items-center gap-2 w-28 shrink-0">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium truncate">{stage.label}</span>
                </div>
                <div className="flex-1 relative h-8">
                  <div
                    className={cn(
                      "h-full rounded-md flex items-center px-3 text-white text-sm font-semibold transition-all duration-500",
                      stage.color
                    )}
                    style={{ width: `${barWidth}%`, minWidth: '40px' }}
                  >
                    {stage.count}
                  </div>
                </div>
                {index < stages.length - 1 && (
                  <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0 hidden sm:block" />
                )}
              </div>
            );
          })}
        </div>

        {/* Conversion rates */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          {stageCounts && stageCounts.matched > 0 && (
            <div className="p-3 bg-muted/50 rounded-lg text-center">
              <p className="text-xs text-muted-foreground">{t('trialSite.components.flowPipeline.screenRate')}</p>
              <p className="text-lg font-bold">
                {Math.round(((stageCounts.screening || 0) / stageCounts.matched) * 100)}%
              </p>
            </div>
          )}
          {stageCounts && stageCounts.screening > 0 && (
            <div className="p-3 bg-muted/50 rounded-lg text-center">
              <p className="text-xs text-muted-foreground">{t('trialSite.components.flowPipeline.enrollRate')}</p>
              <p className="text-lg font-bold">
                {Math.round(((stageCounts.enrolled || 0) / stageCounts.screening) * 100)}%
              </p>
            </div>
          )}
          {stageCounts && stageCounts.enrolled > 0 && (
            <div className="p-3 bg-muted/50 rounded-lg text-center">
              <p className="text-xs text-muted-foreground">{t('trialSite.components.flowPipeline.randomizeRate')}</p>
              <p className="text-lg font-bold">
                {Math.round(((stageCounts.randomized || 0) / stageCounts.enrolled) * 100)}%
              </p>
            </div>
          )}
          {stageCounts && stageCounts.active > 0 && (
            <div className="p-3 bg-muted/50 rounded-lg text-center">
              <p className="text-xs text-muted-foreground">{t('trialSite.components.flowPipeline.completionRate')}</p>
              <p className="text-lg font-bold">
                {Math.round(((stageCounts.completed || 0) / stageCounts.active) * 100)}%
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
